<script setup>
import { ref, onMounted, nextTick, defineEmits } from 'vue'
import LockLogo from '@/components/PhoneOtpLock.vue'
import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n'


import { useSubscriberStore } from '@/stores/subscriber';
import { requestSubscriptionEdit, verifySubscriptionEdit } from '@/api/subscription';



const digits = Array(5).fill('').map(() => ref(''))
const inputs = Array(5).fill(null).map(() => ref(null))

const showSubmitionAnimation = ref(false)
const disabledInputs = ref(false)
const showError = ref(false)

const resendCooldown = ref(60)
const resendDisabled = ref(false)
const showResentMessage = ref(false)

const subscriberStore = useSubscriberStore();


const form = ref({
    lastName: subscriberStore.lastName,
    firstName: subscriberStore.firstName,
    phone: subscriberStore.phone,
    function: subscriberStore.function,
    company: subscriberStore.company,
    hectares: subscriberStore.hectares,
    wilaya: subscriberStore.wilaya,
    commune: subscriberStore.commune,
    cultures: subscriberStore.cultures
});

const { t } = useI18n()

const emit = defineEmits(['toggleLoadingOn', 'toggleLoadingOff', 'toggleSuccessModal']);

onMounted(() => {

    if (!subscriberStore.phone || subscriberStore.phone.trim() === '') {
        router.push('/');
        return;
    }
    nextTick(() => {
        inputs[0]?.focus()
    })
})

const handleInput = (e, index) => {
    const value = e.target.value.replace(/\D/g, '')
    if (value) {
        digits[index].value = value[0]
        if (index < inputs.length - 1) {
            inputs[index + 1]?.focus()
        } else {
            
            const code = digits.map(d => d.value).join('')
            if (code.length === digits.length) {
                submitCode() 
            }
        }
        showError.value = false
    } else {
        digits[index].value = ''
    }
}

const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !digits[index].value && index > 0) {
        inputs[index - 1]?.focus()
    }
}




const submitCode = async () => {
    const code = digits.map(d => d.value).join('');
    emit('toggleLoadingOn');

    try {
        const payload = {
            ...form.value,
            code
        };

        const response = await verifySubscriptionEdit(payload);
        if (response.data.success) {
            emit('toggleSuccessModal');
            setTimeout(() => {
                router.push('edit');
            }, 3000);
        } else {
            toggleSubmitionError(true);
            alert(response.data.message || t('edit_otp.error'));
        }
        emit('toggleLoadingOff');

    } catch (error) {
        toggleSubmitionError(true);
        emit('toggleLoadingOff');
        console.error(t('edit_otp.error'), error);
       
    }
};



const toggleSubmitionError = (active) => {
    showError.value = active
}

const resetCode = () => {
    digits.forEach(d => d.value = '')
    showError.value = false
    inputs[0]?.focus()
}

const startResendCooldown = async () => {
    resendDisabled.value = true;
    resendCooldown.value = 60;
    showResentMessage.value = true;
    resetCode();

    try {
        const response = await requestSubscriptionEdit(form.value);
        if (!response.data.success) {
            resendDisabled.value = false;
            showResentMessage.value = false;
            alert(response.data.message || t('edit_otp.error'));
            return;
        }
    } catch (error) {
        console.error(t('edit_otp.error'), error);
        resendDisabled.value = false;
        showResentMessage.value = false;
        alert(t('edit_otp.error'));
        return;
    }

    const interval = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0) {
            clearInterval(interval);
            resendDisabled.value = false;
            showResentMessage.value = false;
        }
    }, 1000);
};


const router = useRouter();

</script>

<template>
  <div class="w-full h-full flex justify-center items-center relative">
    <div class="w-full lg:w-1/2 h-full flex flex-col justify-center items-center">
      <div class="w-full h-1/3 flex justify-center items-center">
        <LockLogo />
      </div>

      <div class="h-2/3 flex flex-col items-center justify-start mt-2 space-y-2">
        <!-- Title -->
        <div class="text-lg font-medium">{{ $t('edit_otp.title') }}</div>

        <!-- Description -->
        <div class="text-gray-700 text-center w-4/5 lg:w-2/4">
          {{ $t('edit_otp.description') }}
        </div>

        <!-- Code Inputs -->
        <div class="flex justify-center items-center w-full space-x-5 pt-5 px-5 lg:px-0">
          <template v-for="(digit, index) in digits" :key="index">
            <div class="w-1/6 lg:w-1/12">
              <input
                type="text"
                maxlength="1"
                inputmode="numeric"
                pattern="[0-9]*"
                :ref="el => inputs[index] = el"
                v-model="digit.value"
                @input="(e) => handleInput(e, index)"
                @keydown="(e) => handleKeyDown(e, index)"
                :disabled="disabledInputs"
                :class="[
                  'w-full rounded-lg outline-none text-center text-lg shadow px-3 py-4 transition duration-200 border',
                  disabledInputs
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'hover:border-2 focus:border-primary',
                  showError ? 'border-red-300' : 'border-gray-300'
                ]"
              />
            </div>
          </template>
        </div>

        <!-- Error Message -->
        <div v-if="showError" class="text-red-400 text-sm lg:w-3/4 font-medium w-4/5 text-center">
          {{ $t('edit_otp.error') }}
        </div>

        <!-- Resent Message -->
        <div v-if="showResentMessage" class="text-primary text-sm font-medium text-center">
          {{ $t('edit_otp.resent') }}
        </div>

        <!-- Resend Button -->
        <div class="pt-2">
          <button
            class="text-primary font-medium transition duration-200 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
            @click="startResendCooldown"
            :disabled="resendDisabled"
          >
            <span v-if="!resendDisabled">{{ $t('edit_otp.resend') }}</span>
            <span v-else>{{ $t('edit_otp.resend_in', { seconds: resendCooldown }) }}</span>
          </button>
        </div>

        <!-- Submit Button / Loading Animation -->
        <div class="w-full flex justify-center items-center pt-5">
          <div
            :class="[
              showSubmitionAnimation ? 'hidden opacity-0' : 'block opacity-100',
              'bg-primary w-1/2 lg:w-1/3 text-white text-center rounded py-2 px-3 hover:bg-secondary hover:cursor-pointer transition duration-200'
            ]"
            @click="submitCode"
          >
            {{ $t('edit_otp.confirm') }}
          </div>

          <div
            :class="[
              !showSubmitionAnimation ? 'hidden' : 'flex',
              'justify-center items-center space-x-2 mt-4'
            ]"
          >
            <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-0"></span>
            <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-150"></span>
            <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-300"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fade-in {
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.animate-fade-in {
    animation: fade-in 0.3s ease-out;
}

@keyframes bounce-wave {

    0%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-20px);
    }
}

.animate-bounce-wave {
    animation: bounce-wave 1.2s infinite ease-in-out;
}

.delay-0 {
    animation-delay: 0s;
}

.delay-150 {
    animation-delay: 0.15s;
}

.delay-300 {
    animation-delay: 0.3s;
}
</style>
